import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { DEFAULT_ACTIVE_TAB } from './chatteaser3.constants';
import { CHATTEASER3_ANIM_PHASE } from './chatteaser3Anim';
import { useChatteaser3AnimationTimers } from './useChatteaser3AnimationTimers';
import { useChatteaser3RailWheel } from './useChatteaser3RailWheel';

type AnimPhaseLocal = (typeof CHATTEASER3_ANIM_PHASE)[keyof typeof CHATTEASER3_ANIM_PHASE];

export function useChatteaser3AnimationState({
  resetScroll,
  tabCount,
}: {
  resetScroll: () => void;
  tabCount: number;
}) {
  const [activeTab, setActiveTab] = useState(DEFAULT_ACTIVE_TAB);
  const [displayedTab, setDisplayedTab] = useState(DEFAULT_ACTIVE_TAB);
  // Mount starts idle — the whole-component intro (chatteaser3Intro) plays the
  // entrance; per-tab exit/enter only runs on later tab changes.
  const [animPhase, setAnimPhase] = useState<AnimPhaseLocal>(CHATTEASER3_ANIM_PHASE.Idle);
  const activeTabRef = useRef(activeTab);
  const scheduleDisplayedTab = useCallback((value: number): void => {
    setDisplayedTab(value);
  }, []);
  const { clearEnterTimer, clearExitTimer } = useChatteaser3AnimationTimers({
    activeTab,
    displayedTab,
    scheduleDisplayedTab,
    setAnimPhase,
  });
  const startTabTransition = useCallback((index: number): void => {
    if (index === activeTabRef.current) return;
    activeTabRef.current = index;
    clearEnterTimer();
    clearExitTimer();
    setAnimPhase(CHATTEASER3_ANIM_PHASE.Exit);
    setActiveTab(index);
    resetScroll();
  }, [clearEnterTimer, clearExitTimer, resetScroll]);
  const handleSelectTab = useCallback((index: number): void => {
    startTabTransition(index);
  }, [startTabTransition]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const railScrollProgress = useChatteaser3RailWheel({
    activeTabRef,
    startTabTransition,
    tabCount,
  });

  return {
    activeTab,
    animPhase,
    displayedTab,
    handleSelectTab,
    railScrollProgress,
  };
}
